/**
 * LanguageRegionPicker
 * Reusable presentational component for language & region settings.
 * Used by OnboardingScreen (batched local state) and LanguageSettingsScreen (immediate save).
 *
 * Renders (top to bottom):
 * 1. Country picker button → opens CountryPickerModal
 * 2. Variant picker (conditional, only when country has multiple variants)
 * 3. App Language pill toggle (en/es)
 * 4. Lesson Language pill toggle (Español/English)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { View, TouchableOpacity, LayoutAnimation } from 'react-native';
import { HStack, VStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { getCountryDisplayName, getLanguageDisplayName } from '../i18n';
import { colors, FS_SM, FS_MD } from '../theme';
import { CountryPickerModal } from './CountryPickerModal';
import { OptionSelector, OptionPickerModal } from './ui';
import { getCountryConfig, getLocaleOptions } from '../data/countryConfig';
import { getBaseLanguage } from '../types/locale';
import type { LessonLanguage, LocaleMetadataFull } from '../types/locale';
import { useCourseStore } from '../store/courseStore';
import { useDisplayTheme } from '../hooks/useDisplayTheme';

// ---------------------------------------------------------------------------
// Type-safe i18n key maps (literal keys only)
// ---------------------------------------------------------------------------


/**
 * Variant labels and hints — read from store locale metadata (native section),
 * with i18n fallback for offline/first-launch.
 */
function findLocale(locales: LocaleMetadataFull[] | undefined, code: string): LocaleMetadataFull | undefined {
  if (!Array.isArray(locales)) return undefined;
  return locales.find((l) => l.code === code);
}

function getVariantLabel(variant: string, locales: LocaleMetadataFull[]): string {
  const native = findLocale(locales, variant)?.native;
  if (native?.variant) return native.variant;
  return i18n.getFixedT(variant)('native.variant');
}

function getVariantHint(variant: string, locales: LocaleMetadataFull[]): string {
  const native = findLocale(locales, variant)?.native;
  if (native?.variantHint) return native.variantHint;
  return i18n.getFixedT(variant)('native.variantHint');
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LanguageRegionPickerProps {
  country: string | null;
  sourceLocale: string;
  targetLocale: string;
  lessonLanguage: LessonLanguage;
  detectedCountry?: string;
  isLoading?: boolean;

  onCountrySelect: (code: string) => void;
  onVariantSelect: (variant: string) => void;
  onTargetLocaleChange?: (locale: string) => void;
  onLessonLanguageChange: (lang: LessonLanguage) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LanguageRegionPicker({
  country,
  sourceLocale,
  targetLocale,
  lessonLanguage,
  detectedCountry,
  isLoading = false,
  onCountrySelect,
  onVariantSelect,
  onTargetLocaleChange,
  onLessonLanguageChange,
}: LanguageRegionPickerProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const [modalVisible, setModalVisible] = useState(false);

  // Dynamic data from course store
  const courses = useCourseStore((s) => s.courses);
  const localesData = useCourseStore((s) => s.locales);
  const countries = useCourseStore((s) => s.countries);

  const activeRowBg = theme.isDark ? '#1E3A5F' : '#EBF5FF';

  const handleCountrySelect = useCallback(
    (code: string) => {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      onCountrySelect(code);
      setModalVisible(false);
    },
    [onCountrySelect],
  );

  const selectedConfig = country ? getCountryConfig(country, countries) : null;
  const selectedLabel = country
    ? getCountryDisplayName(country, i18n.language)
    : null;

  // Filter to only locales/countries that have a matching course (by source base language)
  const courseSourceBases = useMemo(() => new Set(courses.map((c) => c.sourceBase)), [courses]);
  const countriesWithCourses = useMemo(
    () => countries.filter((c) => c.locales.some((loc) => courseSourceBases.has(getBaseLanguage(loc)))),
    [countries, courseSourceBases],
  );
  const allCountryLocales = country ? getLocaleOptions(country, countries) : [];
  const variantOptions = allCountryLocales.filter((loc) => courseSourceBases.has(getBaseLanguage(loc)));
  const showVariantPicker = variantOptions.length > 1;

  const sourceLang = getBaseLanguage(sourceLocale); // 'es-419' → 'es'
  const targetLang = getBaseLanguage(targetLocale); // 'en-US' → 'en'
  const uiLocale = i18n.language;

  // Target locale variants: match courses by base language, not specific variant (Rule 2)
  const sourceBase = getBaseLanguage(sourceLocale);
  const targetVariantOptions = onTargetLocaleChange
    ? [...new Set(
        courses
          .filter((c) => c.sourceBase === sourceBase)
          .flatMap((c) => c.targetVariants),
      )]
    : [];
  const lessonLangOptions: { value: LessonLanguage; label: string }[] = sourceLocale === ''
    ? []
    : [
        { value: 'source', label: getLanguageDisplayName(sourceLang, uiLocale) },
        { value: 'target', label: getLanguageDisplayName(targetLang, uiLocale) },
      ];

  return (
    <>
      <View style={{ gap: 20 }}>
        {/* 1. Country picker */}
        <VStack space="sm">
          <Text
            allowFontScaling={false}
            style={{
              fontSize: FS_SM * fontScale,
              fontWeight: '600',
              color: theme.textSecondary,
              ...ffs,
            }}
          >
            {t('languageSettings.country')}
          </Text>

          <TouchableOpacity
            testID="language-region-country-picker"
            accessibilityLabel={t('countryPicker.title')}
            accessibilityRole="button"
            onPress={() => setModalVisible(true)}
            activeOpacity={0.7}
            disabled={isLoading}
          >
            <HStack
              p="$4"
              rounded="$lg"
              space="md"
              style={{
                backgroundColor: selectedConfig ? activeRowBg : theme.inputBg,
                borderWidth: 1,
                borderColor: selectedConfig ? colors.primary : theme.border,
                alignItems: 'center',
                opacity: isLoading ? 0.5 : 1,
              }}
            >
              {selectedConfig ? (
                <>
                  <Text allowFontScaling={false} style={{ fontSize: 22 }}>
                    {selectedConfig.emoji}
                  </Text>
                  <Text
                    allowFontScaling={false}
                    style={{
                      fontSize: FS_MD * fontScale,
                      fontWeight: '600',
                      color: colors.primary,
                      flex: 1,
                      ...ffs,
                    }}
                  >
                    {selectedLabel ?? country}
                  </Text>
                </>
              ) : (
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: FS_MD * fontScale,
                    color: theme.textSecondary,
                    flex: 1,
                    ...ffs,
                  }}
                >
                  {t('countryPicker.title')}
                </Text>
              )}
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  color: theme.textSecondary,
                }}
              >
                ▼
              </Text>
            </HStack>
          </TouchableOpacity>
        </VStack>

        {/* 2. Conditional variant picker */}
        {showVariantPicker && (
          <OptionSelector
            label={t('languageSettings.variant')}
            hint={t('languageSettings.variantHint')}
            options={variantOptions.map((v) => ({
              value: v,
              label: getVariantLabel(v, localesData),
              subtitle: getVariantHint(v, localesData),
            }))}
            value={sourceLocale}
            onChange={onVariantSelect}
            layout="card"
            testID="language-region-variant"
            disabled={isLoading}
          />
        )}

        {/* 3. Target locale picker — always modal (labels are long locale names) */}
        {onTargetLocaleChange && targetVariantOptions.length > 1 && (
          <OptionPickerModal
            label={t('languageSettings.targetLocale')}
            modalTitle={t('languageSettings.targetLocale')}
            hint={t('languageSettings.targetLocaleHint')}
            options={targetVariantOptions.map((variant) => {
              const baseLang = getBaseLanguage(variant);
              const region = variant.split('-')[1];
              return {
                value: variant,
                label: `${getLanguageDisplayName(baseLang, uiLocale)} (${getCountryDisplayName(region, uiLocale)})`,
              };
            })}
            value={targetLocale}
            onChange={onTargetLocaleChange}
            testID="language-region-target"
            disabled={isLoading}
          />
        )}

        {/* 4. Lesson Language — hidden until a source language exists
            (onboarding starts with no preselection when the country and OS
            language are unknown; blank pills would be meaningless) */}
        {sourceLocale !== '' && (
          <OptionSelector
            label={t('languageSettings.lessonLanguage')}
            options={lessonLangOptions}
            value={lessonLanguage}
            onChange={onLessonLanguageChange}
            testID="language-region-lesson-lang"
            disabled={isLoading}
          />
        )}
      </View>

      {/* Country picker modal */}
      <CountryPickerModal
        visible={modalVisible}
        detectedCountry={detectedCountry}
        selectedCountry={country}
        countries={countriesWithCourses}
        onSelect={handleCountrySelect}
        onClose={() => setModalVisible(false)}
      />
    </>
  );
}
